import { yupResolver } from '@hookform/resolvers/yup';
// Removed import from @shared/documents
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import useSimpleMutation from '../../hooks/useSimpleMutation';
import { type UnitatDocument, useUnitats } from '../../hooks/useUnitats';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
} from '../Shared/CommonStyles';
import {
	Button,
	FormContainer,
	ErrorMessage as FormErrorMessage,
	FormGroup,
	Input,
	Label,
	Select,
	TextArea,
} from '../Shared/FormStyles';

// Define the types locally
interface EventDocument {
	_id: string;
	title: string;
	description?: string;
	startDate: Date | string;
	endDate: Date | string;
	unitat: string;
}

interface EventFormData {
	title: string;
	description?: string;
	startDate: string;
	endDate?: string;
	unitat: string;
}

const eventSchema = yup.object().shape({
	title: yup
		.string()
		.required('Aquest camp és obligatori')
		.min(2, 'El títol ha de tenir almenys 2 caràcters')
		.max(100, 'El títol no pot tenir més de 100 caràcters'),
	description: yup
		.string()
		.max(1000, 'La descripció no pot tenir més de 1000 caràcters'),
	startDate: yup.string().required('Aquest camp és obligatori'),
	endDate: yup
		.string()
		.optional()
		.test(
			'end_date',
			'La data de fi ha de ser posterior o igual a la d&apos;inici',
			function (value) {
				const { startDate } = this.parent;
				if (!startDate || !value) return true;
				return new Date(value) >= new Date(startDate);
			}
		),
	unitat: yup.string().required('Aquest camp és obligatori'),
});

const EventForm = ({ mode }: { mode: string }) => {
	const queryClient = useQueryClient();
	const { id } = useParams();
	const { user } = useAuth();
	const { unitats, isFetchingUnitats, fetchUnitatsError } = useUnitats();

	const [authError, setAuthError] = useState<string | null>(null);
	const [duration, setDuration] = useState<number>(1);

	const isAuthenticated =
		!!user &&
		(user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') || // trusted curators: any unitat
			user.roles.includes('Unitat'));
	// const isAuthenticated =
	// 	!!user &&
	// 	(user.username.toLowerCase() === unitat ||
	const isEditMode = mode === 'edit';

	// const {
	// 	data: unitats,
	// 	isLoading: isFetchingUnitats,
	// 	error: fetchUnitatsError,
	// } = useFetch<UnitatDocument[]>({
	// 	queryKey: ['unitats'],
	// 	url: '/api/unitats',
	// });

	const {
		data: event,
		isLoading: isFetchingEvent,
		error: fetchEventError,
	} = useFetch<EventDocument>({
		queryKey: ['event', id],
		url: isEditMode && id ? `/api/calendari/events/${id}` : '',
	});

	const {
		data: unitat,
		isLoading: isFetchingUnitat,
		error: fetchUnitatError,
	} = useFetch<UnitatDocument>({
		queryKey: ['unitat', event?.unitat],
		url:
			event?.unitat && event.unitat.toLowerCase() !== 'agrupament'
				? `/api/unitats/${event?.unitat}`
				: '',
	});

	const putMutation = useSimpleMutation<EventFormData>({
		method: 'put',
		url: `/api/calendari/events/${id}`,
		dependencies: [id],
		queriesToInvalidate: [['events']],
		navigateUrl: '/calendari',
	});

	const postMutation = useSimpleMutation<EventFormData>({
		method: 'post',
		url: '/api/calendari/events',
		dependencies: [],
		queriesToInvalidate: [['events']],
		navigateUrl: '/calendari',
	});

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors },
	} = useForm({ resolver: yupResolver(eventSchema) });

	const startDate = watch('startDate');
	const endDate = watch('endDate');

	// Calculate duration when dates change
	useEffect(() => {
		if (startDate && endDate) {
			const start = new Date(startDate);
			const end = new Date(endDate);
			const diffTime = end.getTime() - start.getTime();
			const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
			if (diffDays >= 1 && !isNaN(diffDays)) {
				setDuration(diffDays);
			}
		} else if (startDate && !endDate) {
			setDuration(1);
		}
	}, [startDate, endDate]);

	// Update endDate when duration changes
	const handleDurationChange = (newDuration: number) => {
		setDuration(newDuration);
		if (startDate) {
			const start = new Date(startDate);
			const newEnd = new Date(start);
			newEnd.setDate(start.getDate() + newDuration - 1);
			const endDateStr = newEnd.toISOString().split('T')[0];
			setValue('endDate', endDateStr);
		}
	};

	useEffect(() => {
		if (
			!isEditMode &&
			user &&
			user.unitat &&
			unitats &&
			!user.roles.includes('Admin') &&
			!user.roles.includes('Agrupament') &&
			!user.roles.includes('Caps')
		) {
			const userUnitat = unitats.find((u) => u._id === user.unitat);
			if (userUnitat) {
				setValue('unitat', userUnitat.abreviacio.toLowerCase());
			}
		}
	}, [isEditMode, user, unitats, setValue]);

	const invalidateQueries = () => {
		if (isEditMode) {
			queryClient.invalidateQueries({ queryKey: ['event', id] });
		}
		queryClient.invalidateQueries({ queryKey: ['events'] });
	};

	const onSubmit = async (data: EventFormData) => {
		// If no endDate provided, use startDate (single-day event)
		const payload = {
			...data,
			unitat: data.unitat.toLowerCase(),
			endDate: data.endDate || data.startDate,
		};
		if (isEditMode) {
			putMutation.mutate(payload, {
				onSuccess: () => {
					invalidateQueries();
				},
			});
		} else {
			postMutation.mutate(payload, {
				onSuccess: () => {
					invalidateQueries();
				},
			});
		}
	};

	useEffect(() => {
		if (!user) {
			setAuthError('No estàs autenticat');
		} else if (!isAuthenticated) {
			setAuthError('No tens permís');
		} else {
			setAuthError(null);
		}
	}, [isAuthenticated, user?.username]);

	useEffect(() => {
		if (event) {
			const isAgrupament = event.unitat.toLowerCase() === 'agrupament';

			if (isAgrupament || unitat) {
				const eventFormData = {
					...event,
					startDate: event.startDate.toString().split('T')[0],
					endDate: event.endDate.toString().split('T')[0],
					unitat: event.unitat.toLowerCase(),
				};

				if (
					!(
						user?.roles.includes('Admin') ||
						user?.roles.includes('Agrupament') ||
						user?.roles.includes('Caps')
					)
				) {
					// This should only happen if unitat is defined (not agrupament)
					if (unitat) {
						eventFormData.unitat = unitat.abreviacio.toLowerCase();
					}
				}
				reset(eventFormData);
				
				// Calculate initial duration
				const start = new Date(event.startDate);
				const end = new Date(event.endDate);
				const diffTime = end.getTime() - start.getTime();
				const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
				setDuration(diffDays >= 1 ? diffDays : 1);
			}
		}
	}, [reset, event, user?.roles, unitat]);

	const loading =
		isFetchingUnitats ||
		isFetchingEvent ||
		isFetchingUnitat ||
		putMutation.isPending ||
		postMutation.isPending;

	const error =
		authError ||
		fetchUnitatsError ||
		fetchEventError ||
		fetchUnitatError ||
		putMutation.error ||
		postMutation.error;

	return loading ? (
		<LoadingMessage>
			<LoadingGif size="large" />
		</LoadingMessage>
	) : error ? (
		<ErrorMessage>{extractErrorMessage(error)}</ErrorMessage>
	) : isAuthenticated ? (
		<FormContainer onSubmit={handleSubmit(onSubmit)}>
			{/* <h2>{isEditMode ? 'Editar event' : 'Crear event'}</h2> */}

			<FormGroup>
				<Label htmlFor="title">Títol:</Label>
				<Input id="title" {...register('title')} />
				{errors.title && (
					<FormErrorMessage>
						{errors.title.message?.toString()}
					</FormErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="description">Descripció:</Label>
				<TextArea id="description" {...register('description')} />
				{errors.description && (
					<FormErrorMessage>
						{errors.description.message?.toString()}
					</FormErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="startDate">Data d&apos;inici:</Label>
				<Input id="startDate" type="date" {...register('startDate')} />
				{errors.startDate && (
					<FormErrorMessage>
						{errors.startDate.message?.toString()}
					</FormErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="endDate">Data de fi (opcional):</Label>
				<Input
					id="endDate"
					type="date"
					{...register('endDate')}
				/>
				{errors.endDate && (
					<FormErrorMessage>
						{errors.endDate.message?.toString()}
					</FormErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="duration">Durada (dies):</Label>
				<Input
					id="duration"
					type="number"
					min="1"
					value={duration}
					onChange={(e) => handleDurationChange(parseInt(e.target.value) || 1)}
				/>
			</FormGroup>

			{(user?.roles.includes('Admin') ||
				user?.roles.includes('Agrupament') ||
				user?.roles.includes('Caps')) &&
			!!unitats ? (
				<FormGroup>
					<Label htmlFor="unitat">Unitat:</Label>
					<Select id="unitat" {...register('unitat')}>
						<option value="">Selecciona una unitat</option>
						<option value="agrupament">Agrupament</option>
						{unitats?.map((unitat: UnitatDocument) => (
							<option
								key={unitat._id}
								value={unitat.abreviacio.toLowerCase()}
							>
								{unitat.nom}
							</option>
						))}
					</Select>
					{errors.unitat && (
						<FormErrorMessage>
							{errors.unitat.message?.toString()}
						</FormErrorMessage>
					)}
				</FormGroup>
			) : (
				<input
					type="hidden"
					{...register('unitat')}
					value={user?.unitat}
				/>
			)}

			<Button type="submit" disabled={loading}>
				{loading
					? 'Guardant...'
					: isEditMode
						? 'Desar Canvis'
						: 'Crear Event'}
			</Button>
		</FormContainer>
	) : (
		<div>No s&apos;han pogut carregar les unitats</div>
	);
};

export default EventForm;
